import axios from "axios";
import { browserSupportsWebAuthn, startAuthentication, startRegistration } from "@simplewebauthn/browser";

// Passkeys: sign in with Face ID, Touch ID, a fingerprint or Windows Hello.
// The browser does the passkey work and @simplewebauthn/browser converts
// between its binary values and the base64url text the backend sends and
// expects. The backend checks every signature.
const base = process.env.REACT_APP_SERVER_URL;

export function passkeysSupported() {
  return browserSupportsWebAuthn();
}

/** A friendly default name for a new passkey, from the device in use. */
export function deviceName(agent = typeof navigator !== "undefined" ? navigator.userAgent : "") {
  if (/iPhone/.test(agent)) return "iPhone";
  if (/iPad/.test(agent)) return "iPad";
  if (/Android/.test(agent)) return "Android phone";
  if (/Macintosh|Mac OS X/.test(agent)) return "Mac";
  if (/Windows/.test(agent)) return "Windows PC";
  return "Passkey";
}

/** Plain-language message for a failed passkey step. */
export function passkeyError(err: any, adding = false) {
  if (err?.name === "NotAllowedError" || err?.name === "AbortError") return adding ? "The passkey wasn't added. You can try again any time." : "Passkey sign-in was cancelled. Try again or use your password.";
  if (err?.name === "InvalidStateError") return "This device already has a passkey for your account.";
  if (err?.name === "SecurityError") return "Passkeys only work on technogeniousja.com.";
  const m = err?.response?.data?.message;
  if (typeof m === "string") return m;
  return adding ? "We couldn't add this passkey. Please try again." : "We couldn't sign you in with a passkey. Try again or use your password.";
}

const auth = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } });

export async function addPasskey(token: string, name = deviceName()) {
  const start = (await axios.post(`${base}/passkeys/register/options`, {}, auth(token))).data.data;
  const response = await startRegistration({ optionsJSON: start.options });
  return (await axios.post(`${base}/passkeys/register`, { challengeId: start.challengeId, response, name }, auth(token))).data.data as PasskeyItem;
}

/** Signs in with a passkey; returns the same session as password sign-in. */
export async function signInWithPasskey() {
  const start = (await axios.post(`${base}/passkeys/sign-in/options`, {})).data.data;
  const response = await startAuthentication({ optionsJSON: start.options });
  return (await axios.post(`${base}/passkeys/sign-in`, { challengeId: start.challengeId, response })).data.data;
}

export async function listPasskeys(token: string) { return (await axios.get(`${base}/passkeys`, auth(token))).data.data as PasskeyItem[]; }
export async function removePasskey(token: string, id: string) { await axios.delete(`${base}/passkeys/${encodeURIComponent(id)}`, auth(token)); }
export type PasskeyItem = { id: string; name: string; createdAt: string; lastUsedAt: string | null; synced: boolean };
